import { useQuery } from '@tanstack/react-query';

import {
  getListCompanyBilling,
  getMailingSummary,
  getModelingByList,
  getMultisBreakdown,
  getMultisByGroup,
  getMultisByList,
  getSeedCount,
  getSeedCountByMailCode,
  getStateDistribution,
  getSuppressionDetails,
  getSuppressionFiles,
  getTwentileCount,
  type MergePurgeParameters,
} from '@enterprise/api';
import { ensureError } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import type { MergePurgeView } from '@annotations/mergePurgeView';

import { reportQueryKeys } from '@feat/reports/common/report-query-keys';
import { useApplicationStore } from '@hooks/application/application-store.hook';

/**
 * Custom hook to fetch report data based on the provided view and merge/purge parameters.
 *
 * This hook uses React Query's `useQuery` to asynchronously fetch data from various report endpoints,
 * determined by the `view` parameter. It supports multiple report types such as list company billing,
 * modeling by list, multis breakdown, seed count, suppression details, and more.
 *
 * @template TData - The shape of the data returned for each report type.
 * @param view - The report view type, used to determine which API endpoint to call.
 * @param params - The merge/purge parameters containing `mailingId` and `version`.
 * @returns A React Query result object containing the report data, loading and error states.
 *
 * @example
 * const { data, isLoading, error } = useGetReportData<MyReportType>('summary', { mailingId: 123, version: 1 });
 */
export const useGetReportData = <TData extends Record<string, unknown>>(
  view: MergePurgeView,
  { mailingId, version }: MergePurgeParameters
) => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });

  return useQuery({
    initialData: { success: false } as OperationResultWithData<TData[]>,
    initialDataUpdatedAt: 0,
    queryFn: async () => {
      try {
        switch (view) {
          case 'listCompanyBilling':
            return await getListCompanyBilling(enterpriseApi, { mailingId, version });

          case 'modelingByList':
            return await getModelingByList(enterpriseApi, { mailingId, version });

          case 'multisBreakdown':
            return await getMultisBreakdown(enterpriseApi, { mailingId, version });

          case 'multisByGroup':
            return await getMultisByGroup(enterpriseApi, { mailingId, version });

          case 'multisByList':
            return await getMultisByList(enterpriseApi, { mailingId, version });

          case 'seedCount':
            return await getSeedCount(enterpriseApi, { mailingId, version });

          case 'seedCountByMailCode':
            return await getSeedCountByMailCode(enterpriseApi, { mailingId, version });

          case 'stateDistribution':
            return await getStateDistribution(enterpriseApi, { mailingId, version });

          case 'summary':
            return await getMailingSummary(enterpriseApi, { mailingId, version });

          case 'suppressionDetail':
            return await getSuppressionDetails(enterpriseApi, { mailingId, version });

          case 'suppressionFile':
            return await getSuppressionFiles(enterpriseApi, { mailingId, version });

          case 'twentileCount':
            return await getTwentileCount(enterpriseApi, { mailingId, version });

          default:
            return { success: false } as OperationResultWithData<TData[]>;
        }
      } catch (e) {
        const error = ensureError(e);

        console.error(error);

        return { success: false } as OperationResultWithData<TData[]>;
      }
    },
    queryKey: reportQueryKeys.mergePurge.report(view, mailingId, version),
    retry: true,
    select: (result) => {
      return result as OperationResultWithData<TData[]> & { dynamicColumnCount?: number };
    },
  });
};
